import { useMutation } from "@apollo/client/react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import {
  PublicConfirmReservationDocument,
  PublicConfirmReservationErrorEnum,
  PublicReservationStatus,
  type GetPublicReservationQuery,
} from "@/graphql/graphql"
import { usePublicReservation } from "@/graphql/hooks"
import { showErrorToast } from "@/shared/toast"

export type InterstitialMode = "loading" | "celebrate" | "hide" | null

type Reservation = NonNullable<GetPublicReservationQuery["public"]["reservation"]>
type RefetchReservation = ReturnType<typeof usePublicReservation>["refetch"]

type UseConfirmationFlowProps = {
  reservationId: string | undefined
  confirmToken: string | null
  reservation: Reservation | null | undefined
  refetch: RefetchReservation
}

const buildReservationPath = (reservationId: string) => `/r/${reservationId}`

const resolveInterstitialMode = async ({
  refetch,
  reservation,
  confirmedStatus,
}: {
  refetch: RefetchReservation
  reservation: Reservation
  confirmedStatus: PublicReservationStatus
}) => {
  try {
    const refetchResult = await refetch()
    const refreshedStatus = refetchResult.data?.public?.reservation?.status ?? confirmedStatus

    return reservation.status !== PublicReservationStatus.Confirmed &&
      refreshedStatus === PublicReservationStatus.Confirmed
      ? "celebrate"
      : "hide"
  } catch (error) {
    console.error("Failed to refresh public reservation after confirmation.", error)

    return reservation.status !== PublicReservationStatus.Confirmed &&
      confirmedStatus === PublicReservationStatus.Confirmed
      ? "celebrate"
      : "hide"
  }
}

export function useConfirmationFlow({ reservationId, confirmToken, reservation, refetch }: UseConfirmationFlowProps) {
  const navigate = useNavigate()
  const confirmAttemptedRef = useRef(false)
  const [interstitialMode, setInterstitialMode] = useState<InterstitialMode>(confirmToken ? "loading" : null)
  const [confirmReservation] = useMutation(PublicConfirmReservationDocument)

  useEffect(() => {
    if (!confirmToken || !reservationId || !reservation || confirmAttemptedRef.current) return

    confirmAttemptedRef.current = true

    void confirmReservation({
      variables: { reservationId, token: confirmToken },
    })
      .then(async (result) => {
        const confirmationResult = result.data?.publicConfirmReservation
        const errors = confirmationResult?.errors ?? []
        const errorCode = errors[0]?.code

        if (
          errorCode === PublicConfirmReservationErrorEnum.Expired ||
          errorCode === PublicConfirmReservationErrorEnum.NotConfirmable
        ) {
          navigate(`/v/${reservation.venue.slug}?confirmation=expired`, { replace: true })
          return
        }

        if (errorCode === PublicConfirmReservationErrorEnum.Invalid) {
          navigate(`/v/${reservation.venue.slug}?confirmation=invalid`, { replace: true })
          return
        }

        const confirmedReservation = confirmationResult?.reservation

        if (!confirmedReservation || confirmedReservation.status !== PublicReservationStatus.Confirmed) {
          showErrorToast("We could not confirm your reservation. Please try again.")
          console.error("Public reservation confirmation did not return a confirmed reservation.", confirmationResult)
          setInterstitialMode("hide")
          navigate(buildReservationPath(reservationId), { replace: true })
          return
        }

        setInterstitialMode("loading")
        navigate(buildReservationPath(reservationId), { replace: true })

        const nextInterstitialMode = await resolveInterstitialMode({
          refetch,
          reservation,
          confirmedStatus: confirmedReservation.status,
        })

        setInterstitialMode(nextInterstitialMode)
        return undefined
      })
      .catch((error) => {
        showErrorToast()
        console.error("Failed to confirm public reservation.", error)
        setInterstitialMode("hide")
        navigate(buildReservationPath(reservationId), { replace: true })
      })
  }, [confirmReservation, confirmToken, navigate, refetch, reservation, reservationId])

  return { interstitialMode, setInterstitialMode }
}
